import type { VerifiedDeploymentHandoff } from './pendingVerificationSession'

const STORAGE_KEY = 'peltast:registration-session-startup'
export const REGISTRATION_STARTUP_QUEUED_EVENT = 'peltast:registration-startup-queued'

export interface RegistrationSessionStartup {
  serverUuid: string
  intentUuid?: string
  queuedAt: string
}

export type RegistrationStartupDecision = 'complete' | 'start' | 'wait' | 'stop'
export type RegistrationStartupFailureDecision = 'retry' | 'stop'

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function queueRegistrationSessionStartup(handoff: VerifiedDeploymentHandoff): void {
  if (!handoff.server_uuid || !['project-zomboid', 'minecraft'].includes(handoff.game_slug)) return
  const pending: RegistrationSessionStartup = {
    serverUuid: handoff.server_uuid,
    intentUuid: handoff.intent_uuid || undefined,
    queuedAt: new Date().toISOString(),
  }
  storage()?.setItem(STORAGE_KEY, JSON.stringify(pending))
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(REGISTRATION_STARTUP_QUEUED_EVENT))
}

export function loadRegistrationSessionStartup(): RegistrationSessionStartup | null {
  const raw = storage()?.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const pending = JSON.parse(raw) as RegistrationSessionStartup
    if (!pending.serverUuid) throw new Error('Missing server UUID')
    return pending
  } catch {
    clearRegistrationSessionStartup()
    return null
  }
}

export function clearRegistrationSessionStartup(): void {
  storage()?.removeItem(STORAGE_KEY)
}

export function registrationStartupDecision(input: {
  installing: boolean
  suspended: boolean
  powerState?: string | null
  resourceObservationStatus?: number
}): RegistrationStartupDecision {
  if (input.suspended) return 'stop'
  if (input.installing) return 'wait'
  const state = String(input.powerState || '').toLowerCase()
  if (state === 'running') return 'complete'
  if (state === 'offline' || state === 'stopped') return 'start'
  // During package trial provisioning Wings deliberately withholds customer
  // service reads while its signed lease can still allow the initial start.
  // The ordinary power endpoint remains the authority for that decision.
  if (input.resourceObservationStatus === 403) return 'start'
  return 'wait'
}

export function registrationStartupFailureDecision(status: number): RegistrationStartupFailureDecision {
  return status === 401 || status === 403 || status === 404 ? 'stop' : 'retry'
}

export function registrationStartupRetryDelay(attempt: number): number {
  return Math.min(30_000, 2_000 * (2 ** Math.max(0, attempt - 1)))
}
